import { cn } from "@omni/utils/styles";
import { FieldValues, Path } from "react-hook-form";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

export type RadioFieldArrayOption<TOption> = {
    label: string;
    value: TOption;
    disabled?: boolean;
};

type RadioFieldArrayProperties<TOption> = {
    disabled?: boolean;
    direction?: "horizontal" | "vertical";
    renderLabel?: (
        option: RadioFieldArrayOption<TOption>,
        index: number
    ) => React.ReactNode;
    options: Array<RadioFieldArrayOption<TOption>>;
};

type RadioFieldArrayProps<
    T extends FieldValues,
    TOption
> = FormValuesExploded<T> & RadioFieldArrayProperties<TOption>;

const RadioFieldArray = <
    T extends FieldValues,
    TOption extends string | number
>({
    name,
    label,
    microCopy,
    required,
    hideLabel,
    hideMicroCopy,
    disabled,
    direction = "horizontal",
    renderLabel,
    className,
    options
}: RadioFieldArrayProps<T, TOption>) => {
    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            className={className}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
        >
            {({ field }) => {
                return (
                    <div
                        className={cn("tw:flex tw:gap-4", {
                            "tw:flex-col": direction === "vertical"
                        })}
                    >
                        {options.map((option, index) => (
                            <FormGroup
                                key={`radio-${option.value}-${index}`}
                                toggle={true}
                                inline={direction === "horizontal"}
                            >
                                <Label>
                                    <Input
                                        {...field}
                                        type={"radio"}
                                        value={option.value}
                                        checked={field.value === option.value}
                                        disabled={disabled || option.disabled}
                                    />
                                    {renderLabel
                                        ? renderLabel(option, index)
                                        : option.label}
                                </Label>
                            </FormGroup>
                        ))}
                    </div>
                );
            }}
        </BaseField>
    );
};

export { RadioFieldArray };
